import type { GroupBase, StylesConfig } from 'react-select';
import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import {
  getInputBackgroundColor,
  getInputBorderColor,
} from '@/components/Input/Input.styles';
import type { SelectOption, SelectProps } from '@/components/Select';
import { theme } from '@/theme/getTheme';

export const defaultVariant: StylesConfig<
  SelectOption,
  false,
  GroupBase<SelectOption>
> = {
  container: (styles) => ({
    ...styles,
    width: '100%',
    pointerEvents: 'auto',
  }),
  control: (styles, { isDisabled, isFocused, selectProps }) => ({
    ...styles,
    border: 'none',
    borderBottom: `1px solid ${getInputBorderColor(
      (selectProps as SelectProps).borderColorVariant,
    )}`,
    borderRadius: 'none',
    padding: theme.spacing(0, 1, 0, 0),
    fontSize: theme.fontSizes.p1,
    backgroundColor: getInputBackgroundColor(
      (selectProps as SelectProps).colorVariant,
    ),
    ...(isFocused && {
      '&:hover': {
        outline: 'none',
      },
    }),
    ...(isDisabled && {
      '&:hover': {
        cursor: 'not-allowed',
      },
    }),
    boxShadow: 'none',
    input: {
      '&:focus': {
        outline: '1px solid transparent !important',
      },
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: () => ({
    color: theme.colors.primary,
  }),
  menu: (styles) => ({
    ...styles,
    borderRadius: 'none',
    marginTop: -1,
    border: `1px solid ${theme.colors.secondaryGrey}`,
    zIndex: `${theme.zIndex.sortingDropdown}`,
  }),
  menuList: (styles) => ({
    ...styles,
    paddingTop: 0,
    paddingBottom: 0,
    overflowX: 'hidden',
  }),
  option: (styles, { isFocused }) => ({
    ...styles,
    height: 45,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: theme.colors.primaryBlue,
    backgroundColor: isFocused ? theme.colors.lightGrey : 'none',
    fontSize: theme.fontSizes.p1,
    '&:hover': {
      background: theme.colors.lightGrey,
    },
  }),
  singleValue: (styles) => ({
    ...styles,
    paddingBlock: 2,
  }),
};

export const boxedVariant: StylesConfig<
  SelectOption,
  false,
  GroupBase<SelectOption>
> = {
  container: (styles) => ({
    ...styles,
    width: '100%',
  }),
  control: (styles, { isFocused }) => ({
    ...styles,
    borderRadius: 'none',
    textTransform: 'uppercase',
    height: 47,
    border: `2px solid ${theme.colors.primaryBlue}`,
    fontWeight: theme.fontWeights.bold,
    fontSize: theme.fontSizes.p1,
    boxShadow: 'none',
    ...(isFocused && {
      '&:hover': {
        outline: 'none',
      },
    }),
    input: {
      '&:focus': {
        outline: '1px solid transparent !important',
      },
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: () => ({
    color: theme.colors.primaryBlue,
    paddingRight: theme.spacing(1),
  }),
  menu: (styles) => ({
    ...styles,
    border: `2px solid ${theme.colors.primaryBlue}`,
    borderRadius: 'none',
    marginTop: -1,
  }),
  menuList: (styles) => ({
    ...styles,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  option: (styles, { isFocused }) => ({
    ...styles,
    height: 45,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: theme.colors.primaryBlue,
    backgroundColor: isFocused ? theme.colors.lightGrey : 'none',

    '&:hover': {
      background: theme.colors.lightGrey,
    },
  }),
};
export const transparentVariant: StylesConfig<
  SelectOption,
  false,
  GroupBase<SelectOption>
> = {
  container: (styles) => ({
    ...styles,
    width: '100%',
  }),
  control: (styles, { isFocused }) => ({
    ...styles,
    borderRadius: 'none',
    textTransform: 'uppercase',
    height: 47,
    border: `2px solid ${theme.colors.white}`,
    fontWeight: theme.fontWeights.bold,
    fontSize: theme.fontSizes.p1,
    boxShadow: 'none',
    backgroundColor: theme.colors.transparent,
    '&:hover': {
      border: `2px solid ${theme.colors.white}`,
    },
    ...(isFocused && {
      '&:hover': {
        outline: 'none',
      },
    }),
    input: {
      '&:focus': {
        outline: '1px solid transparent !important',
      },
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: () => ({
    color: theme.colors.primaryBlue,
    paddingRight: theme.spacing(1),
    '& > svg': {
      height: '26px',
      width: '26px',
    },
    '& > svg path': {
      fill: `${theme.colors.primary}`,
    },
  }),
  menu: (styles) => ({
    ...styles,
    border: `2px solid ${theme.colors.primaryBlue}`,
    borderRadius: 'none',
    marginTop: -1,
  }),
  menuList: (styles) => ({
    ...styles,
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: theme.colors.white,
  }),
  option: (styles, { isFocused }) => ({
    ...styles,
    height: 45,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: theme.colors.primaryBlue,
    backgroundColor: isFocused ? theme.colors.lightGrey : 'none',
    '&:hover': {
      background: theme.colors.lightGrey,
    },
  }),
  singleValue: (styles) => ({
    ...styles,
    color: theme.colors.white,
  }),
};
export const IconContainer = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;
  z-index: 1;
  width: 24px;
  background: ${({ theme }) => theme.colors.lightGrey};
`;

export const LabelWrapper = styled(Box)<Pick<SelectProps, 'colorVariant'>>`
  white-space: nowrap;

  ${({ colorVariant, theme }) => css`
    ${(colorVariant === 'white' || colorVariant === 'defaultInverted') &&
    css`
      color: ${theme.colors.white};
    `};
  `};
`;
